import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowLeft, ArrowRight, Clock, Calendar, Moon, Sun,
  Lock, Timer, LockOpen, User, LogOut, Heart, Share2
} from "lucide-react";
import { blog as allPosts } from "../data/content";
import { useAuth }          from "../auth/AuthContext";
import AuthModal            from "../auth/AuthModal";
import AccountManager       from "../auth/AccountManager";
import { getStats, incrementStat, decrementLikes } from "../lib/supabaseStats";
import { BlogComments }     from "../components/blog/BlogComments";
import { KofiButton }       from "../components/blog/KofiWidget";
import { WelcomePopup }     from "../components/blog/WelcomePopup";
import { SupportPopup }     from "../components/blog/SupportPopup";

/* ─── localStorage helpers ───────────────────────────────────── */
const LS_LIKES      = "mc_blog_likes";
const LS_LIKE_CNTS  = "mc_blog_like_counts";
const LS_SHARE_CNTS = "mc_blog_share_counts";
function loadLikes()        { try { return JSON.parse(localStorage.getItem(LS_LIKES)      || "{}"); } catch { return {}; } }
function saveLikes(l)       { try { localStorage.setItem(LS_LIKES,      JSON.stringify(l)); } catch {} }
function loadLikeCounts()   { try { return JSON.parse(localStorage.getItem(LS_LIKE_CNTS)  || "{}"); } catch { return {}; } }
function saveLikeCounts(c)  { try { localStorage.setItem(LS_LIKE_CNTS,  JSON.stringify(c)); } catch {} }
function loadShareCounts()  { try { return JSON.parse(localStorage.getItem(LS_SHARE_CNTS) || "{}"); } catch { return {}; } }
function saveShareCounts(c) { try { localStorage.setItem(LS_SHARE_CNTS, JSON.stringify(c)); } catch {} }

const BLOG_THEME_KEY = "mc_blog_theme";

function parseDateISO(iso) {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : 0;
}

function getAccess(post) {
  // "scheduled" auto-upgrades to "open" once unlockAt has passed.
  const raw = post.access || (
    post.lockMode === "unlocked" ? "open" :
    post.lockMode === "locked"   ? "members" :
    post.lockMode === "timer"    ? "scheduled" : "open"
  );
  if (raw === "scheduled" && post.unlockAt) {
    if (Date.now() >= new Date(post.unlockAt).getTime()) return "open";
  }
  return raw;
}

/* ─── Badge / accent components ─────────────────────────────── */
function AccessBadge({ access, scheduledDate }) {
  if (access === "open") {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full px-2 py-[2px] font-mono"
        style={{ fontSize: 9, letterSpacing: "0.06em", background: "var(--blog-badge-open-bg)", color: "var(--blog-badge-open-color)", border: "0.5px solid var(--blog-badge-open-border)" }}
      >
        <LockOpen className="w-2.5 h-2.5" /> Open
      </span>
    );
  }
  if (access === "members") {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full px-2 py-[2px] font-mono"
        style={{ fontSize: 9, letterSpacing: "0.06em", background: "var(--blog-badge-locked-bg)", color: "var(--blog-badge-locked-color)", border: "0.5px solid var(--blog-badge-locked-border)" }}
      >
        <Lock className="w-2.5 h-2.5" /> Members
      </span>
    );
  }
  let label = "Soon";
  if (scheduledDate) {
    try { label = new Date(scheduledDate).toLocaleDateString("en-US", { month: "short", day: "numeric" }); } catch (_) {}
  }
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-[2px] font-mono"
      style={{ fontSize: 9, letterSpacing: "0.06em", background: "var(--blog-badge-timer-bg)", color: "var(--blog-badge-timer-color)", border: "0.5px solid var(--blog-badge-timer-border)" }}
    >
      <Timer className="w-2.5 h-2.5" /> {label}
    </span>
  );
}

function AccentDash({ access }) {
  const color = access === "members" ? "var(--blog-accent-magenta)" : access === "scheduled" ? "var(--blog-accent-amber)" : "var(--blog-accent-teal)";
  return <span className="inline-block" style={{ width: 18, height: "1.5px", background: color, flexShrink: 0 }} />;
}

function bodyArrayToMarkdown(body) {
  if (!Array.isArray(body)) return "";
  return body.map((b) => {
    if (b.type === "h2")   return `## ${b.text}`;
    if (b.type === "h3")   return `### ${b.text}`;
    if (b.type === "code") return "```\n" + b.text + "\n```";
    return b.text;
  }).join("\n\n");
}

function ScheduledNotice({ post }) {
  let formatted = post.scheduledDate;
  try { formatted = new Date(post.scheduledDate).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }); } catch (_) {}
  return (
    <div data-testid="post-scheduled-notice" className="rounded-md p-8 my-8 text-center" style={{ background: "var(--blog-bg-tertiary)", border: "0.5px solid var(--blog-border-secondary)" }}>
      <Timer className="w-7 h-7 mx-auto mb-3" style={{ color: "var(--blog-accent-amber)" }} />
      <div className="font-display font-medium mb-1" style={{ fontSize: 18, color: "var(--blog-text-primary)" }}>Scheduled for {formatted}</div>
      <div style={{ fontSize: 13, color: "var(--blog-text-tertiary)" }}>This essay isn't out yet. Check back on the date above.</div>
    </div>
  );
}

function MembersPaywall({ post, onSignIn, onJoin }) {
  return (
    <div data-testid="post-paywall" className="rounded-md p-8 my-8 text-center" style={{ background: "var(--blog-bg-tertiary)", border: "0.5px solid var(--blog-border-secondary)" }}>
      <Lock className="w-7 h-7 mx-auto mb-3" style={{ color: "var(--blog-accent-magenta)" }} />
      <div className="font-display font-medium mb-2" style={{ fontSize: 22, color: "var(--blog-text-primary)", letterSpacing: "-0.01em" }}>Members-only essay</div>
      <p className="mx-auto mb-6" style={{ fontSize: 13, color: "var(--blog-text-tertiary)", lineHeight: 1.6, maxWidth: 360 }}>
        "{post.title}" is reserved for members. Sign in to keep reading and access future members-only posts.
      </p>
      <div className="flex items-center justify-center gap-2 flex-wrap">
        <button onClick={onSignIn} data-testid="post-paywall-cta" className="rounded-md" style={{ background: "var(--blog-text-primary)", color: "var(--blog-bg-primary)", border: "none", padding: "11px 18px", fontSize: 12, fontWeight: 500, letterSpacing: "0.04em", cursor: "pointer" }}>Sign in to read</button>
        <button onClick={onJoin}   data-testid="post-paywall-signup" className="rounded-md" style={{ background: "transparent", color: "var(--blog-text-secondary)", border: "0.5px solid var(--blog-border-primary)", padding: "11px 18px", fontSize: 12, fontWeight: 500, letterSpacing: "0.04em", cursor: "pointer" }}>Create free account</button>
      </div>
    </div>
  );
}

/* ─── Floating reading nav ───────────────────────────────────── */
const FLOAT_NAV_STYLES = `
@keyframes fnSlideDown {
  from { opacity: 0; transform: translateY(-10px); }
  to   { opacity: 1; transform: translateY(0);     }
}
.fn-enter { animation: fnSlideDown 220ms cubic-bezier(0.34,1.2,0.64,1) forwards; }
@keyframes fnTitleIn {
  from { opacity: 0; transform: translateX(10px); }
  to   { opacity: 1; transform: translateX(0);    }
}
.fn-title-enter { animation: fnTitleIn 180ms ease forwards; }
`;

function FloatingReadingNav({ post, isDark, setTheme, user, logout, onOpenAuth, readProgress, showTitle }) {
  return (
    <>
      <style>{FLOAT_NAV_STYLES}</style>
      <div
        className="fn-enter blog-theme"
        data-blog-theme={isDark ? "dark" : "light"}
        style={{
          position:          "fixed",
          top:               0,
          left:              0,
          right:             0,
          zIndex:            850,
          background:        isDark ? "rgba(10,10,12,0.92)" : "rgba(237,235,229,0.94)",
          transition:        "background 200ms ease, border-color 200ms ease",
          backdropFilter:    "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          borderBottom:      "0.5px solid var(--blog-border-tertiary)",
          fontFamily:        "'Inter', system-ui, sans-serif",
        }}
      >
        {/* Reading progress bar */}
        <div
          style={{
            height:     2,
            background: "var(--blog-accent-teal)",
            width:      `${readProgress}%`,
            transition: "width 120ms linear",
          }}
        />

        {/* position:relative so the title can be absolutely centred
            relative to the full nav, not the unequal gap between logo + controls */}
        <div
          style={{
            position:       "relative",
            display:        "flex",
            alignItems:     "center",
            justifyContent: "space-between",
            padding:        "0 20px",
            height:         50,
          }}
        >
          {/* Left: logo */}
          <Link
            to="/"
            className="font-display font-medium uppercase tracking-[0.12em]"
            style={{ fontSize: 11, color: "var(--blog-text-secondary)", flexShrink: 0, position: "relative", zIndex: 1 }}
          >
            Malicke Case
          </Link>

          {/* Centre: truly centred title  -  position:absolute ignores
              the differing widths of logo and controls. */}
          {showTitle && post?.title && (
            <div
              style={{
                position:       "absolute",
                left:           0,
                right:          0,
                top:            0,
                bottom:         0,
                display:        "flex",
                alignItems:     "center",
                justifyContent: "center",
                pointerEvents:  "none",
                padding:        "0 160px", // stay clear of logo + controls
              }}
            >
              <span
                key={post.title}
                className="fn-title-enter font-display font-medium"
                style={{
                  fontSize:     12,
                  color:        "var(--blog-text-primary)",
                  whiteSpace:   "nowrap",
                  overflow:     "hidden",
                  textOverflow: "ellipsis",
                  maxWidth:     "100%",
                  letterSpacing:"-0.005em",
                }}
              >
                {post.title}
              </span>
            </div>
          )}

          {/* Right: controls */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, position: "relative", zIndex: 1 }}>
            <button
              onClick={() => setTheme(isDark ? "light" : "dark")}
              aria-label="Toggle theme"
              style={{
                display:    "flex",
                alignItems: "center",
                gap:        5,
                background: "transparent",
                border:     "0.5px solid var(--blog-border-primary)",
                borderRadius: 20,
                padding:    "5px 10px",
                fontSize:   10,
                letterSpacing: "0.06em",
                color:      "var(--blog-text-secondary)",
                cursor:     "pointer",
              }}
            >
              {isDark ? <Sun className="w-3 h-3" /> : <Moon className="w-3 h-3" />}
              <span className="hidden sm:inline">{isDark ? "Light" : "Dark"}</span>
            </button>

            {user ? (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 10, color: "var(--blog-text-secondary)", display: "flex", alignItems: "center", gap: 4 }}>
                  <User className="w-3 h-3" />
                  <span className="hidden sm:inline">{user.name || user.email}</span>
                </span>
                <button
                  onClick={logout}
                  title="Sign out"
                  style={{ background: "transparent", border: "none", color: "var(--blog-text-tertiary)", cursor: "pointer", padding: 3 }}
                >
                  <LogOut className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <button
                onClick={onOpenAuth}
                style={{
                  display:    "flex",
                  alignItems: "center",
                  gap:        5,
                  background: "transparent",
                  border:     "0.5px solid var(--blog-border-primary)",
                  borderRadius: 20,
                  padding:    "5px 10px",
                  fontSize:   10,
                  color:      "var(--blog-text-primary)",
                  cursor:     "pointer",
                  letterSpacing: "0.04em",
                }}
              >
                <User className="w-3 h-3" />
                <span className="hidden sm:inline">Sign in</span>
              </button>
            )}

          </div>
        </div>
      </div>
    </>
  );
}

function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const fn = () => setShow(window.scrollY > 400);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  if (!show) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Back to top"
      style={{
        position: "fixed", bottom: 28, right: 28, zIndex: 999,
        width: 44, height: 44, borderRadius: "50%",
        background: "#0E3A3A", color: "#fff", border: "none",
        cursor: "pointer", fontSize: 18, lineHeight: 1,
        boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
      }}
    >↑</button>
  );
}

/* ─── Main page ──────────────────────────────────────────────── */
export default function BlogPostPage() {
  const { slug }     = useParams();
  const navigate     = useNavigate();
  const { user, loading, logout } = useAuth();
  const [authOpen,   setAuthOpen]   = useState(false);
  const [authMode,   setAuthMode]   = useState("login");
  const [theme,      setTheme]      = useState(() => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(BLOG_THEME_KEY) || "light";
  });

  /* Likes & shares */
  const [liked,      setLiked]      = useState(false);
  const [likeCount,  setLikeCount]  = useState(0);
  const [shareCount, setShareCount] = useState(0);
  const [copied,     setCopied]     = useState(false);

  /* Floating nav state */
  const [isNavFloating,  setIsNavFloating]  = useState(false);
  const [showTitleInNav, setShowTitleInNav] = useState(false);
  const [readProgress,   setReadProgress]   = useState(0);
  const inPageNavRef = useRef(null);
  const postTitleRef = useRef(null);

  /* Derived post data */
  const sorted = useMemo(() => allPosts.slice().sort((a, b) => parseDateISO(b.dateISO) - parseDateISO(a.dateISO)), []);
  const post   = useMemo(() => allPosts.find((p) => p.slug === slug), [slug]);
  const idx    = useMemo(() => sorted.findIndex((p) => p.slug === slug), [sorted, slug]);
  const prev   = idx > 0 ? sorted[idx - 1] : null;
  const next   = idx >= 0 && idx < sorted.length - 1 ? sorted[idx + 1] : null;

  useEffect(() => {
    document.documentElement.classList.remove("dark");
    window.scrollTo(0, 0);
    if (post?.title) document.title = `${post.title} \u2022 Malicke Case`;
    return () => {
      document.documentElement.classList.add("dark");
      document.title = "Malicke Case";
    };
  }, [post]);

  useEffect(() => {
    try { localStorage.setItem(BLOG_THEME_KEY, theme); } catch (_) {}
  }, [theme]);

  /* Scroll listener: floating nav + reading progress */
  useEffect(() => {
    function onScroll() {
      if (inPageNavRef.current) {
        const navBottom = inPageNavRef.current.getBoundingClientRect().bottom;
        setIsNavFloating(navBottom < 0);
      }
      if (postTitleRef.current) {
        const titleBottom = postTitleRef.current.getBoundingClientRect().bottom;
        setShowTitleInNav(titleBottom < 60); // 60px = floating nav height
      }
      const total = document.documentElement.scrollHeight - window.innerHeight;
      if (total > 0) setReadProgress(Math.round((window.scrollY / total) * 100));
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Load likes / shares  -  show localStorage values instantly, patch with remote */
  useEffect(() => {
    if (!post) return;
    const key   = post.slug || post.id;
    const likes = loadLikes();
    const lc    = loadLikeCounts();
    const sc    = loadShareCounts();

    // Instant: paint counts from localStorage so UI is never blank.
    setLiked(!!likes[key]);
    setLikeCount(lc[key] !== undefined ? lc[key] : (likes[key] ? 1 : 0));
    setShareCount(sc[key] !== undefined ? sc[key] : 0);

    // Background: overwrite with authoritative Supabase values when ready.
    getStats(key).then((remote) => {
      if (remote) {
        setLikeCount(remote.likes);
        setShareCount(remote.shares);
      }
    });
  }, [post?.slug, post?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleLike() {
    if (!post) return;
    const key  = post.slug || post.id;
    const next = !liked;
    setLiked(next);
    const likes = loadLikes(); likes[key] = next; saveLikes(likes);
    setLikeCount((c) => next ? c + 1 : Math.max(0, c - 1));
    const lc = loadLikeCounts(); lc[key] = (lc[key] || 0) + (next ? 1 : -1); saveLikeCounts(lc);
    if (next) { incrementStat(key, "likes"); } else { decrementLikes(key); }
  }

  function handleShare() {
    if (!post) return;
    const key = post.slug || post.id;
    const url = window.location.href;
    function doShare() {
      setShareCount((c) => c + 1);
      const sc = loadShareCounts(); sc[key] = (sc[key] || 0) + 1; saveShareCounts(sc);
      incrementStat(key, "shares");
    }
    if (navigator.share) {
      navigator.share({ title: post.title, url }).then(doShare).catch(() => {});
    } else {
      navigator.clipboard.writeText(url).then(() => {
        doShare(); setCopied(true); setTimeout(() => setCopied(false), 2200);
      }).catch(() => {});
    }
  }

  const isDark  = theme === "dark";
  const pageBg  = isDark ? "#050506" : "#E6E2DA";

  if (!post) {
    return (
      <div className="blog-theme min-h-screen flex items-center justify-center" data-blog-theme={isDark ? "dark" : "light"} data-testid="blog-post-not-found"
        style={{ background: isDark ? "#050506" : "#E6E2DA", fontFamily: "'Inter', system-ui, sans-serif", padding: 32 }}>
        <div className="text-center">
          <div className="font-display font-medium mb-4" style={{ fontSize: 28, color: "var(--blog-text-primary)" }}>Post not found</div>
          <p className="mb-6" style={{ color: "var(--blog-text-tertiary)" }}>We couldn't find an essay at <code>/blog/{slug}</code>.</p>
          <button onClick={() => navigate("/blog")} className="rounded-md px-4 py-2" style={{ fontSize: 12, background: "var(--blog-text-primary)", color: "var(--blog-bg-primary)", border: "none" }}>
            ← Back to Journal
          </button>
        </div>
      </div>
    );
  }

  const access    = getAccess(post);
  const contentMd = post.content && post.content.trim().length > 0 ? post.content : bodyArrayToMarkdown(post.body);

  return (
    <div
      className="blog-theme min-h-screen"
      data-blog-theme={isDark ? "dark" : "light"}
      data-testid="blog-post-page"
      style={{
        background:  pageBg,
        fontFamily:  "'Inter', system-ui, sans-serif",
        transition:  "background 200ms ease",
        padding:     "32px 16px 64px",
      }}
    >
      {/* ── Floating reading nav (appears on scroll) ──────────── */}
      {isNavFloating && (
        <FloatingReadingNav
          post={post}
          isDark={isDark}
          setTheme={setTheme}
          user={user}
          logout={logout}
          onOpenAuth={() => { setAuthMode("login"); setAuthOpen(true); }}
          readProgress={readProgress}
          showTitle={showTitleInNav}
        />
      )}

      {/* ── Static top controls (hidden when floating nav active) */}
      <div className="mx-auto flex justify-end gap-[10px] mb-1" style={{ width: "100%", maxWidth: 980, visibility: isNavFloating ? "hidden" : "visible" }}>
        <button
          data-testid="post-theme-toggle"
          onClick={() => setTheme(isDark ? "light" : "dark")}
          className="inline-flex items-center gap-2 rounded-full px-[14px] py-[6px]"
          style={{ fontSize: 11, letterSpacing: "0.06em", border: "1px solid var(--blog-border-primary)", background: "var(--blog-bg-primary)", color: "var(--blog-text-secondary)", cursor: "pointer" }}
        >
          {isDark ? <Sun className="w-3 h-3" /> : <Moon className="w-3 h-3" />}
          {isDark ? "Light mode" : "Dark mode"}
        </button>
      </div>

      <article
        className="mx-auto overflow-hidden"
        style={{
          width:        "100%",
          maxWidth:     980,
          background:   "var(--blog-bg-primary)",
          borderRadius: 12,
          border:       "0.5px solid var(--blog-border-primary)",
          transition:   "background 200ms ease",
        }}
      >
        {/* ── In-page nav (gets ref'd for scroll detection) ───── */}
        <nav
          ref={inPageNavRef}
          className="px-7 pt-[14px] pb-[14px]"
          style={{ borderBottom: "0.5px solid var(--blog-border-tertiary)" }}
        >
          {/* Row 1: brand + account */}
          <div className="flex items-center justify-between">
            <Link to="/" className="font-display font-medium uppercase tracking-[0.12em]" style={{ fontSize: 12, color: "var(--blog-text-secondary)" }}>
              Malicke Case
            </Link>
            {/* Desktop center links */}
            <div className="hidden sm:flex gap-6">
              <Link to="/blog/about" className="uppercase" style={{ fontSize: 11, letterSpacing: "0.06em", color: "var(--blog-text-tertiary)" }}>About</Link>
              <Link to="/blog"       className="uppercase" style={{ fontSize: 11, letterSpacing: "0.06em", color: "var(--blog-text-primary)"   }}>Journal</Link>
              <Link to="/blog/contact" className="uppercase" style={{ fontSize: 11, letterSpacing: "0.06em", color: "var(--blog-text-tertiary)" }}>Contact</Link>
            </div>
  {user ? (
              <AccountManager />
            ) : (
              <button
                data-testid="post-nav-login"
                onClick={() => setAuthOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-full"
                style={{ fontSize: 11, letterSpacing: "0.04em", padding: "6px 14px", border: "0.5px solid var(--blog-border-primary)", background: "var(--blog-bg-secondary)", color: "var(--blog-text-primary)", cursor: "pointer", fontFamily: "inherit" }}
              >
                <User className="w-3 h-3" /> Sign in
              </button>
            )}
          </div>
          {/* Mobile-only second row: nav links centered */}
          <div className="sm:hidden flex items-center justify-center gap-5 pt-3">
            <Link to="/blog/about" className="uppercase" style={{ fontSize: 10, letterSpacing: "0.10em", color: "var(--blog-text-tertiary)" }}>About</Link>
            <span style={{ color: "var(--blog-border-secondary)", fontSize: 10 }}>·</span>
            <Link to="/blog" className="uppercase" style={{ fontSize: 10, letterSpacing: "0.10em", color: "var(--blog-text-primary)" }}>Journal</Link>
            <span style={{ color: "var(--blog-border-secondary)", fontSize: 10 }}>·</span>
            <Link to="/blog/contact" className="uppercase" style={{ fontSize: 10, letterSpacing: "0.10em", color: "var(--blog-text-tertiary)" }}>Contact</Link>
          </div>
        </nav>

        {/* ── Cover hero image ── */}
        {post.coverImage && (
          <div style={{ width: "100%", height: 260, overflow: "hidden", position: "relative", flexShrink: 0 }}>
            <img
              src={post.coverImage}
              alt={post.title}
              loading="eager"
              fetchPriority="high"
              decoding="sync"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center 30%",
                filter: "contrast(1.1) brightness(0.78) saturate(0.80)"
              }}
            />
            <div style={{
              position: "absolute", inset: 0,
              background: "linear-gradient(to bottom, transparent 65%, var(--blog-bg-primary) 98%)"
            }} />
          </div>
        )}

        {/* ── Post header ──────────────────────────────────────── */}
        <header className="px-6 sm:px-12 lg:px-20 pt-10 pb-8" style={{ borderBottom: "0.5px solid var(--blog-border-tertiary)" }}>
          <Link to="/blog" data-testid="post-back-to-journal" className="inline-flex items-center gap-[6px] uppercase mb-6"
            style={{ fontSize: 11, letterSpacing: "0.04em", color: "var(--blog-text-tertiary)" }}>
            <ArrowLeft className="w-3 h-3" /> Back to Journal
          </Link>

          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <AccentDash access={access} />
            <span className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: "0.10em", color: "var(--blog-text-tertiary)" }}>
              {post.category || post.tags?.[0] || "Writing"}
            </span>
            <AccessBadge access={access} scheduledDate={post.scheduledDate} />
          </div>

          {/* ← this h1 is observed to trigger title in floating nav */}
          <h1
            ref={postTitleRef}
            className="font-display font-medium mb-3"
            style={{ fontSize: "clamp(24px, 4vw, 34px)", lineHeight: 1.15, letterSpacing: "-0.01em", color: "var(--blog-text-primary)", maxWidth: 680 }}
            data-testid="post-title"
          >
            {post.title}
          </h1>

          <div className="flex items-center gap-3 flex-wrap" style={{ fontSize: 11, color: "var(--blog-text-tertiary)" }}>
            <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {post.date}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readTime} read</span>
            {post.tags && post.tags.length > 0 && (
              <>
                <span>·</span>
                <span className="inline-flex items-center gap-2 flex-wrap">
                  {post.tags.map((t) => (
                    <span key={t} className="rounded-full px-2 py-[2px] font-mono uppercase"
                      style={{ fontSize: 9, letterSpacing: "0.08em", border: "0.5px solid var(--blog-border-secondary)", color: "var(--blog-text-tertiary)" }}>
                      {t}
                    </span>
                  ))}
                </span>
              </>
            )}
          </div>
        </header>

        {/* ── Post body ────────────────────────────────────────── */}
        <div className="px-6 sm:px-12 lg:px-20 pt-10 pb-16" style={{ maxWidth: 820, margin: "0 auto" }} data-testid="post-content">
          {access === "scheduled" ? (
            <ScheduledNotice post={post} />
          ) : access === "members" && loading ? (
            <div className="rounded-md p-8 my-8 text-center" style={{ background: "var(--blog-bg-tertiary)", border: "0.5px solid var(--blog-border-secondary)", color: "var(--blog-text-tertiary)" }}>
              Checking membership...
            </div>
          ) : access === "members" && !user ? (
            <MembersPaywall post={post}
              onSignIn={() => { setAuthMode("login");  setAuthOpen(true); }}
              onJoin={()   => { setAuthMode("signup"); setAuthOpen(true); }}
            />
          ) : (
            <div className="blog-prose">
              {/* ReactMarkdown v8+ escapes raw HTML by default  -  no rehype-raw plugin, safe for editor-controlled content */}
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{contentMd}</ReactMarkdown>
            </div>
          )}
        </div>

        {/* ── Prev / Next ──────────────────────────────────────── */}
        {(prev || next) && (
          <div className="flex items-center justify-between px-7 py-5"
            style={{ borderTop: "0.5px solid var(--blog-border-tertiary)", borderBottom: "0.5px solid var(--blog-border-tertiary)" }}>
            {prev ? (
              <Link to={`/blog/${prev.slug}`} data-testid="post-prev-link" className="flex items-center gap-2 group" style={{ color: "var(--blog-text-tertiary)" }}>
                <ArrowLeft className="w-3.5 h-3.5" />
                <div className="text-left">
                  <div className="uppercase mb-[2px]" style={{ fontSize: 9, letterSpacing: "0.10em" }}>Previous</div>
                  <div className="font-display font-medium" style={{ fontSize: 12, color: "var(--blog-text-primary)" }}>{prev.title}</div>
                </div>
              </Link>
            ) : <span />}
            {next ? (
              <Link to={`/blog/${next.slug}`} data-testid="post-next-link" className="flex items-center gap-2 group" style={{ color: "var(--blog-text-tertiary)" }}>
                <div className="text-right">
                  <div className="uppercase mb-[2px]" style={{ fontSize: 9, letterSpacing: "0.10em" }}>Next</div>
                  <div className="font-display font-medium" style={{ fontSize: 12, color: "var(--blog-text-primary)" }}>{next.title}</div>
                </div>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            ) : <span />}
          </div>
        )}

        {/* ── Like / Share bar ─────────────────────────────────── */}
        <div className="px-6 sm:px-12 lg:px-20 pb-10" style={{ maxWidth: 820, margin: "0 auto" }}>
          <div className="flex items-center justify-between" style={{ paddingTop: 24 }}>
            <button data-testid="post-like-btn" onClick={toggleLike} className="inline-flex items-center gap-2 rounded-md"
              style={{
                background: liked ? "var(--blog-badge-locked-bg)" : "transparent",
                color:      liked ? "var(--blog-accent-magenta)" : "var(--blog-text-tertiary)",
                border:     liked ? "0.5px solid var(--blog-badge-locked-border)" : "0.5px solid var(--blog-border-secondary)",
                padding: "9px 16px", fontSize: 11, fontWeight: 500, letterSpacing: "0.06em",
                cursor: "pointer", fontFamily: "inherit", transition: "all 150ms ease"
              }}>
              <Heart className="w-3.5 h-3.5" style={{ fill: liked ? "var(--blog-accent-magenta)" : "none" }} />
              {liked ? "Liked" : "Like"}
              <span style={{ opacity: 0.55, fontVariantNumeric: "tabular-nums" }}>{likeCount > 0 ? ` ${likeCount}` : ""}</span>
            </button>

            <button data-testid="post-share-btn" onClick={handleShare} className="inline-flex items-center gap-2 rounded-md"
              style={{
                background: "transparent",
                color: copied ? "var(--blog-accent-teal)" : "var(--blog-text-tertiary)",
                border: "0.5px solid var(--blog-border-secondary)",
                padding: "9px 16px", fontSize: 11, fontWeight: 500, letterSpacing: "0.06em",
                cursor: "pointer", fontFamily: "inherit", transition: "color 150ms ease"
              }}>
              <Share2 className="w-3.5 h-3.5" />
              {copied ? "Link copied!" : "Share"}
              <span style={{ opacity: 0.55, fontVariantNumeric: "tabular-nums" }}>{shareCount > 0 ? ` ${shareCount}` : ""}</span>
            </button>
          </div>
        </div>

        {/* ── Comments ─────────────────────────────────────────── */}
        {(access !== "scheduled") && (access !== "members" || user) && (
          <div className="px-6 sm:px-12 lg:px-20 pb-16" style={{ maxWidth: 820, margin: "0 auto" }} data-testid="post-comments">
            <div style={{ paddingTop: 32, marginBottom: 16 }}>
              <div className="font-mono uppercase mb-1" style={{ fontSize: 10, letterSpacing: "0.10em", color: "var(--blog-accent-teal)" }}>Discussion</div>
              <div className="font-display font-medium mb-6" style={{ fontSize: 18, color: "var(--blog-text-primary)", letterSpacing: "-0.01em" }}>Comments</div>
            </div>
            <BlogComments postId={post.slug} isDark={isDark} />
          </div>
        )}
      </article>

      {/* ── Auth modal ───────────────────────────────────────────── */}
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} defaultMode={authMode} title={access === "members" ? "Members" : undefined} />

      {/* ── Ko-fi floating button ────────────────────────────────── */}
      <KofiButton isDark={isDark} />
      <BackToTop />

      {/* ── Welcome popup (non-logged-in / non-subscribed visitors) ─ */}
      <WelcomePopup
        isDark={isDark}
        onOpenSignup={() => { setAuthMode("signup"); setAuthOpen(true); }}
        onOpenNewsletter={() => {
          // Scroll to newsletter strip on the blog list page, or just open signup
          setAuthMode("signup"); setAuthOpen(true);
        }}
      />

      {/* ── Funny support popup (fires after 30s or 70% scroll) ───── */}
      <SupportPopup
        isDark={isDark}
        isMembersLocked={access === "members" && !user && !loading}
        onLike={access !== "members" || user ? toggleLike : undefined}
      />
    </div>
  );
}
